<script>
import AddressForm from "@/components/address/AddressForm";
import CreditCardForm from "@/components/payment/CreditCardForm";
import settingsService from "@/services/Settings/settingsService";

import { formatCurrency } from "@/formatter/NumberFormatter";

import moment from "moment";
import { required } from "vuelidate/lib/validators";

export default {
  name: "payment-method",
  props: {
    title: {
      type: String
    },
    initialData: {
      type: Object,
      required: false
    }
  },
  components: {
    AddressForm,
    CreditCardForm
  },
  data() {
    return {
      value: {
        paymentKind: "pix",
        address: {},
        creditCard: {},
        tariff: 1,
        ...this.initialData,
        isDoacao: process.env.VUE_APP_CHECKOUT === "doacao"
      },

      metodosPagamento: {
        cartaoCredito: false,
        ted: false,
        boleto: false,
        pix: false
      }
    };
  },
  validations: {
    value: {
      address: {
        valid: (address, vm) => {
          if (vm.paymentKind !== "bankslip") return true;
          return (
            required(address.publicPlace) &&
            required(address.number) &&
            required(address.district) &&
            required(address.city) &&
            required(address.state)
          );
        }
      },
      creditCard: {
        valid: (creditCard, vm) => {
          if (vm.paymentKind !== "creditcard") return true;
          return (
            required(creditCard.cardNumber) &&
            required(creditCard.holder) &&
            required(creditCard.securityCode) &&
            required(creditCard.expirationDate)
          );
        },
        expirationDate: {
          valid: value => {
            if (value === "" || value === undefined) return false;
            var expireAt = moment(value, "MM/YYYY").startOf("day");
            return expireAt
              .endOf("month")
              .isSameOrAfter(moment().endOf("month"));
          }
        }
      }
    }
  },
  computed: {
    amount() {
      return formatCurrency(this.value.amount);
    },
    tarifa() {
      return formatCurrency(this.value.tariff);
    },
    total() {
      return formatCurrency(this.value.amount + this.value.tariff);
    }
  },
  async mounted() {
    this.amountFormatted = this.value.amount;
    await this.setupPaymentMethod();
  },
  methods: {
    async setupPaymentMethod() {
      let safe2PaySettings = await settingsService.getIntegracaoSafe2PaySettings();
      this.metodosPagamento.cartaoCredito =
        safe2PaySettings.credit_card.is_enabled &&
        this.value.amount >= safe2PaySettings.credit_card.min_value &&
        this.value.amount <= safe2PaySettings.credit_card.max_value;

      this.metodosPagamento.boleto =
        safe2PaySettings.doc.is_enabled &&
        this.value.amount >= safe2PaySettings.doc.min_value &&
        this.value.amount <= safe2PaySettings.doc.max_value;

      this.metodosPagamento.ted =
        safe2PaySettings.ted.is_enabled &&
        this.value.amount >= safe2PaySettings.ted.min_value &&
        this.value.amount <= safe2PaySettings.ted.max_value;

      this.metodosPagamento.pix =
        safe2PaySettings.pix.is_enabled &&
        this.value.amount >= safe2PaySettings.pix.min_value &&
        this.value.amount <= safe2PaySettings.pix.max_value;

      this.value.paymentKind = this.metodosPagamento.pix
        ? "pix"
        : this.metodosPagamento.cartaoCredito
        ? "creditcard"
        : "bankslip";
    },

    makePayment() {
      this.$emit("make-payment", this.value);
    },

    handleAddress(address) {
      this.value = {
        ...this.value,
        address
      };
    },
    handleCreditCard(creditCard) {
      this.value = {
        ...this.value,
        creditCard
      };
    },
    setPaymentKind(paymentKind) {
      this.value = {
        ...this.value,
        paymentKind
      };
    }
  }
};
</script>

<template>
  <div>
    <div class="row col-md-12" style="margin-bottom: 25px;">
      <h3 slot="header" class="title">
        Forma de pagamento
      </h3>
    </div>

    <div class="form-row" v-if="value.amount">
      <div class="form-group col-md-12" style="margin-bottom: 25px;">
        <h6 slot="header" class="title">Valor de Contribuição: {{ amount }}</h6>
        <h6 slot="header" class="title">VALOR DE TARIFA: {{ tarifa }}</h6>
        <h6 slot="header" class="title">TOTAL: {{ total }}</h6>
      </div>
    </div>

    <div class="form-row">
      <div class="form-group col-md-12">
        <div
          id="formFormaPagamento"
          class="btn-group col-md-12"
          role="group"
          aria-label="Forma de pagamento"
        >
          <button
            v-if="metodosPagamento.boleto"
            type="button"
            :class="
              value.paymentKind === 'bankslip'
                ? 'btn btn-outline-dark active'
                : 'btn btn-outline-dark'
            "
            @click="setPaymentKind('bankslip')"
          >
            Boleto
          </button>
          <button
            v-if="metodosPagamento.cartaoCredito"
            type="button"
            :class="
              value.paymentKind === 'creditcard'
                ? 'btn btn-outline-dark active'
                : 'btn btn-outline-dark'
            "
            @click="setPaymentKind('creditcard')"
          >
            Cartão de Crédito
          </button>
          <button
            v-if="metodosPagamento.pix"
            type="button"
            :class="
              value.paymentKind === 'pix'
                ? 'btn btn-outline-dark active'
                : 'btn btn-outline-dark'
            "
            @click="setPaymentKind('pix')"
          >
            PIX
          </button>
        </div>
      </div>
    </div>
    <div>
      <div v-if="value.paymentKind === 'bankslip' && metodosPagamento.boleto">
        <address-form @change="handleAddress" :initialValue="value.address" />
        <div style="color: red" v-if="$v.value.address.$invalid">
          Informe todos os campos obrigatórios.
        </div>
      </div>
      <div
        v-if="
          value.paymentKind === 'creditcard' && metodosPagamento.cartaoCredito
        "
      >
        <credit-card-form
          @change="handleCreditCard"
          :value="value.creditCard"
        />
        <div style="color: red" v-if="$v.value.creditCard.$invalid">
          Informe corretamente todos os campos.
        </div>
      </div>
      <div v-if="value.paymentKind === 'pix' && metodosPagamento.pix">
        <div>
          Pagar com PIX.
        </div>
      </div>

      <div class="form-row">
        <div class="form-group col-md-12">
          <button
            class="btn btn-success btn-lg col-md-12"
            style="background-color: #17a2b8; border-color: #17a2b8;"
            :disabled="
              (value.paymentKind === 'bankslip' && $v.value.address.$invalid) ||
                (value.paymentKind === 'creditcard' &&
                  $v.value.creditCard.$invalid)
            "
            v-on:click="makePayment"
          >
            FINALIZAR
          </button>
        </div>
      </div>
    </div>
  </div>
</template>
